import React, { useCallback, useMemo } from 'react';

import { createDocumentRoute } from '@aline/common';
import { useSnackbar } from 'notistack';
import { LoadingSnackbar } from 'src/components';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { CopyIcon } from 'src/components/icons/CopyIcon';
import { DownloadIcon } from 'src/components/icons/DownloadIcon';
import { PrintIcon } from 'src/components/icons/PrintIcon';
import { RenameIcon } from 'src/components/icons/RenameIcon';
import { ShareIcon } from 'src/components/icons/ShareIcon';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { useDownloadDOCXWithTrackedChangesCheck } from 'src/editor/toolbars/menus';
import { Apollo } from 'src/gql/generated';
import { openLink, useDocumentParams } from 'src/helpers';
import {
  useCopyDocument,
  useDownloadFileVersionWithLoading,
  useGetCurrentDoc,
  useGoToLatestVersion,
} from 'src/hooks';
import { useEditor } from 'src/layout-engine/hooks';
import { v4 as uuid4 } from 'uuid';

import { shareDialogOpenVar } from '../../AlineEditor';
import { plannedOnly, useIfEditableMixin, useIfLoggedInMixin } from '../shared/menu-mixins';

export function useFileMenu() {
  const { copyDocument, loading: duplicateLoading } = useCopyDocument();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const {
    documentUi: { setPageSetupDialogOpen, setRenameDoc, setTrashDialogOpen },
  } = useEditorTabStateActions();
  const [disableStorables] = Apollo.useDisableStorablesMutation();
  const { document } = useGetCurrentDoc();
  const { documentId } = useDocumentParams();
  const { downloadDOCXWithTrackedChangesCheck } = useDownloadDOCXWithTrackedChangesCheck();
  const { downloadFileVersionWithLoading } = useDownloadFileVersionWithLoading();
  const editor = useEditor();
  const { goToLatestVersion } = useGoToLatestVersion();
  const ifEditable = useIfEditableMixin();
  const ifLoggedIn = useIfLoggedInMixin();

  const downloadDOCX = useCallback(() => {
    downloadDOCXWithTrackedChangesCheck(documentId, {
      docParts: editor.children,
      name: document?.name || 'Untitled document',
    });
  }, [document?.name, documentId, downloadDOCXWithTrackedChangesCheck, editor]);

  const downloadPDF = useCallback(() => {
    if (document?.latestVersion)
      downloadFileVersionWithLoading(documentId, {
        exportName: document.name,
        fileVersion: 'latestVersion',
      });
  }, [document?.latestVersion, document?.name, documentId, downloadFileVersionWithLoading]);

  const duplicate = useCallback(async () => {
    if (!document) return;
    const snackKey = uuid4();
    enqueueSnackbar('', {
      content: <LoadingSnackbar id={snackKey} message="Duplicating document..." />,
      key: snackKey,
      persist: true,
    });
    const { data } = await copyDocument({
      variables: {
        documentId: document.id,
        documentProps: {
          name: `${document.name} (Copy)`,
        },
      },
    });
    closeSnackbar(snackKey);
    if (data?.copyDocument.document) {
      const route = createDocumentRoute(data.copyDocument.document.id);
      openLink(route);
      enqueueSnackbar('Document duplicated! Opened in a new tab.', { variant: 'success' });
    }
  }, [closeSnackbar, copyDocument, document, enqueueSnackbar]);

  const moveToTrash = useCallback(async () => {
    if (!document) return;
    await disableStorables({ variables: { disableProps: { storableIds: [document.id] } } });
    setTrashDialogOpen(true);
  }, [disableStorables, document, setTrashDialogOpen]);

  const share = useCallback(() => shareDialogOpenVar(true), []);

  return useMemo(
    (): DeclarativeMenuConfig => [
      { icon: <ShareIcon />, label: 'Share...', onClick: share },
      DECLARATIVE_MENU_SEPARATOR,
      { label: 'Open', ...ifLoggedIn, ...plannedOnly },
      {
        icon: <DownloadIcon />,
        label: 'Download as',
        submenu: [
          { label: 'Microsoft Word (.docx)', onClick: downloadDOCX },
          { label: 'PDF (.pdf)', onClick: downloadPDF },
        ],
      },
      { label: 'Version history', onClick: goToLatestVersion },
      DECLARATIVE_MENU_SEPARATOR,
      {
        icon: <RenameIcon />,
        label: 'Rename',
        onClick: () => setRenameDoc(true),
        ...ifEditable,
      },
      { enabled: !duplicateLoading, icon: <CopyIcon />, label: 'Duplicate', onClick: duplicate },
      { icon: <TrashcanIcon />, label: 'Move to trash...', onClick: moveToTrash, ...ifEditable },
      DECLARATIVE_MENU_SEPARATOR,
      {
        label: 'Page setup...',
        onClick: () => setPageSetupDialogOpen(true),
      },
      { icon: <PrintIcon />, label: 'Print...', ...plannedOnly },
    ],
    [
      downloadDOCX,
      downloadPDF,
      duplicate,
      duplicateLoading,
      goToLatestVersion,
      ifEditable,
      ifLoggedIn,
      moveToTrash,
      setPageSetupDialogOpen,
      setRenameDoc,
      share,
    ]
  );
}
